/**
 * Das Kontomenü am Fuß der Seitenleiste.
 *
 * **Es ist die einzige Ausnahme von der Regel, dass die Seitenleiste nur die
 * Anlage führt.** Alles darüber — Übersicht, Strom, Geräte, Einstellungen —
 * bezieht sich auf die im Umschalter gewählte Anlage. Was darüber hinausgeht,
 * gehört dem Konto und nicht der Anlage: Profil, Feedback, Abmelden. Stünde das
 * in derselben Liste, hieße „Einstellungen" mal das eine und mal das andere.
 *
 * **Die Seiten des Plattform-Admins stehen hier nicht einzeln**, sondern als
 * ein Eintrag „Administration": Er führt in den Admin-Bereich mit eigener
 * Leiste (`/admin/…`). Einzeln wuchs hier mit jeder Admin-Seite eine zweite
 * Navigation heran.
 *
 * **Die Anlagenverwaltung gehört ausdrücklich nicht hierher**, obwohl sie dem
 * Konto gehört: Sie hängt am Umschalter, weil man eine weitere Anlage dort
 * sucht, wo die bestehenden stehen — nicht unten beim Profil.
 *
 * Aufbau und Tastaturverhalten wie im `SiteSwitcher`: Escape schließt und gibt
 * den Fokus zurück, ein Klick daneben schließt, die Einträge sind echte
 * Schaltflächen. Das Menü klappt **nach oben** auf — es sitzt am unteren Rand,
 * und nach unten wäre kein Platz.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { SidebarUser, icons, useSidebarCollapsed } from "@hausmeter/ui";
import { useAvatarUrl } from "../lib/avatar.js";

export interface AccountMenuEntry {
  readonly key: string;
  readonly label: string;
  readonly icon?: ReactNode;
  /** Setzt den Eintrag unter eine Trennlinie — für „Abmelden". */
  readonly separated?: boolean;
  readonly onSelect: () => void;
}

export interface AccountMenuProps {
  readonly name: string;
  readonly detail: string;
  /**
   * Wann das Profilbild zuletzt gesetzt wurde; `null` heißt „keins". Ändert es
   * sich, lädt die Zeile das Bild neu — der Zeitstempel ist das Signal.
   */
  readonly avatarUpdatedAt: string | null;
  /**
   * Die Initialen als Ersatz für das Bild, solange keines hochgeladen ist —
   * `null`, wenn auch kein Name hinterlegt ist. Dann steht dort das
   * gezeichnete Symbol.
   */
  readonly initials: string | null;
  /** Welcher Eintrag gerade den Inhaltsbereich füllt, falls einer. */
  readonly activeKey: string | null;
  readonly entries: readonly AccountMenuEntry[];
  /**
   * Eine Einstellung, die im Menü selbst bedient wird — das Erscheinungsbild
   * (HM-107). Sie steht unter einer eigenen Überschrift zwischen den Bereichen
   * und den abgesetzten Einträgen, und **das Menü bleibt dabei offen**: Wer
   * „Dunkel" wählt, will sehen, was er gewählt hat, und dann vielleicht noch
   * einmal wechseln.
   */
  readonly appearance?: ReactNode;
}

export function AccountMenu({
  name,
  detail,
  avatarUpdatedAt,
  initials,
  activeKey,
  entries,
  appearance,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLDivElement>(null);
  const avatarUrl = useAvatarUrl(avatarUpdatedAt);
  const collapsed = useSidebarCollapsed();

  /**
   * Wo das Menü steht, wenn die Leiste eingeklappt ist.
   *
   * **Eingeklappt muss es aus der Leiste heraus**, und dafür reicht `absolute`
   * nicht: Die Leiste ist 64 Pixel breit, das Gerüst darum trägt
   * `overflow: hidden` — ein Menü, das rechts über die Kante ragt, wäre dort
   * abgeschnitten. `fixed` steht außerhalb jeder dieser Kanten, verlangt dafür
   * aber Zahlen, und die kommen aus der Zeile selbst.
   *
   * **Gemessen wird beim Öffnen.** Ein `ResizeObserver` wäre die genauere und
   * die falsche Antwort: Das Menü lebt Sekunden, und was seine Lage ändern
   * könnte — Fenstergröße, Ein- und Ausklappen — schließt es ohnehin.
   */
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null);

  function openMenu(): void {
    const rect = trigger.current?.getBoundingClientRect();
    setAnchor(
      rect === undefined
        ? null
        : { left: rect.right + 8, bottom: window.innerHeight - rect.bottom },
    );
    setOpen(true);
  }

  /* Ein Wechsel der Leistenbreite schließt: Das Menü hinge sonst neben einer
     Kante, die es nicht mehr gibt. */
  useEffect(() => {
    setOpen(false);
  }, [collapsed]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (container.current && !container.current.contains(event.target as Node)) setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Ohne das steht der Fokus nach dem Schließen am Seitenanfang, und die
      // Tastatur beginnt von vorn.
      trigger.current?.querySelector("button")?.focus();
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={container} style={{ position: "relative" }}>
      <div ref={trigger}>
        <SidebarUser
          /**
           * Das Profilbild, sofern eines da ist — sonst das gezeichnete Symbol.
           * Der Rahmen ist rund und beschnitten (`SidebarUser` setzt das), das
           * Bild deshalb `cover`: Ein anderes Seitenverhältnis würde sonst
           * gestaucht, und der Zuschnitt hat schon stattgefunden.
           */
          icon={
            avatarUrl !== null ? (
              <img
                src={avatarUrl}
                alt=""
                width={34}
                height={34}
                style={{ objectFit: "cover" }}
              />
            ) : (
              /* Ohne Bild die Initialen, sonst das gezeichnete Symbol —
                 dieselbe Staffelung wie in der Kopfkarte des Profils. */
              (initials ?? <icons.IconUser size={17} />)
            )
          }
          name={name}
          /* Eingeklappt bleibt vom Namen nur die `aria-label` — siehe
             `SidebarUser.label`. */
          label={name}
          detail={detail}
          expanded={open}
          onClick={() => (open ? setOpen(false) : openMenu())}
          /* Reine Anzeige — in einer Schaltfläche darf keine zweite stehen. */
          action={<icons.IconUnfold size={15} style={{ color: "var(--text-faint)" }} />}
        />
      </div>

      {open && (
        <div
          role="menu"
          aria-label="Konto"
          style={{
            /* Ausgeklappt hängt es an der Zeile und füllt die Breite der
               Leiste; eingeklappt steht es daneben und braucht seine eigene. */
            ...(collapsed && anchor !== null
              ? /* Breit genug für die drei Wahlen des Erscheinungsbilds nebeneinander. */
                { position: "fixed" as const, left: anchor.left, bottom: anchor.bottom, minWidth: 236 }
              : {
                  position: "absolute" as const,
                  /* Nach oben, nicht nach unten: Die Zeile sitzt am unteren
                     Rand der Leiste. */
                  bottom: "calc(100% + 10px)",
                  left: 0,
                  right: 0,
                }),
            zIndex: 60,
            padding: 5,
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          {entries.map((entry) => {
            const active = entry.key === activeKey;

            return (
              <div key={entry.key}>
                {entry.separated === true && appearance !== undefined && (
                  <div style={{ padding: "4px 4px 6px" }}>
                    <div
                      style={{ height: 1, background: "var(--border-subtle)", margin: "1px 0 9px" }}
                    />
                    <div
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--text-faint)",
                        padding: "0 5px 6px",
                      }}
                    >
                      Erscheinungsbild
                    </div>
                    {appearance}
                  </div>
                )}
                {entry.separated === true && (
                  <div
                    style={{ height: 1, background: "var(--border-subtle)", margin: "5px 4px" }}
                  />
                )}
                <button
                  type="button"
                  role="menuitem"
                  aria-current={active ? "page" : undefined}
                  onClick={() => {
                    entry.onSelect();
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "8px 9px",
                    border: "none",
                    borderRadius: "var(--radius-sm)",
                    fontFamily: "var(--font-body)",
                    fontSize: 13.5,
                    fontWeight: 500,
                    textAlign: "left",
                    cursor: "pointer",
                    color: active ? "var(--hm-green-700)" : "var(--text-body)",
                    background: active ? "var(--hm-green-50)" : "transparent",
                  }}
                >
                  {entry.icon !== undefined && (
                    <span
                      style={{
                        display: "inline-flex",
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        color: active ? "var(--hm-green-600)" : "var(--text-muted)",
                      }}
                    >
                      {entry.icon}
                    </span>
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>{entry.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
